import { Node, Edge } from 'reactflow';

interface IValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
}

/**
 * 验证流程图的基本结构
 */
export const validateFlowStructure = (
    nodes: Node[],
    edges: Edge[]
): IValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 检查是否有节点
    if (nodes.length === 0) {
        errors.push('Flow must have at least one node');
        return { isValid: false, errors, warnings };
    }

    // 检查是否有开始节点
    const startNodes = nodes.filter(node => node.type === 'start');
    if (startNodes.length === 0) {
        errors.push('Flow must have at least one start node');
    } else if (startNodes.length > 1) {
        warnings.push('Multiple start nodes detected');
    }

    // 检查是否有问题节点
    const questionNodes = nodes.filter(node => node.type === 'question');
    if (questionNodes.length === 0) {
        errors.push('Flow must have at least one question node');
    }

    // 检查是否有结果节点
    const resultNodes = nodes.filter(node => node.type === 'result');
    if (resultNodes.length === 0) {
        errors.push('Flow must have at least one result node');
    }

    // 检查孤立节点
    const connectedNodeIds = new Set<string>();
    edges.forEach(edge => {
        connectedNodeIds.add(edge.source);
        connectedNodeIds.add(edge.target);
    });

    const isolatedNodes = nodes.filter(node => 
        node.type !== 'start' && !connectedNodeIds.has(node.id)
    );
    
    if (isolatedNodes.length > 0) {
        warnings.push(`Found ${isolatedNodes.length} isolated nodes`);
    }

    // 检查循环引用
    const hasCycle = detectCycle(nodes, edges);
    if (hasCycle) {
        warnings.push('Circular references detected in the flow');
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
};

/**
 * 验证节点配置
 */
export const validateNodeConfigurations = (
    nodes: Node[],
    nodeConfigs: Record<string, any>
): IValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    nodes.forEach(node => {
        const config = nodeConfigs[node.id];
        
        if (!config) {
            errors.push(`Node ${node.id} is missing configuration`);
            return;
        }

        // 验证问题节点配置
        if (node.type === 'question') {
            if (!config.questionId) {
                errors.push(`Question node ${node.id} is missing questionId`);
            }
            if (!config.answerCondition) {
                warnings.push(`Question node ${node.id} has no answer condition`);
            }
        }

        // 验证结果节点配置
        if (node.type === 'result') {
            if (!config.resultType) {
                errors.push(`Result node ${node.id} is missing resultType`);
            }
            
            if (config.resultType === 'continue' && !config.skipToQuestionId) {
                errors.push(`Continue result node ${node.id} is missing skipToQuestionId`);
            }
            
            if (config.resultType === 'add_note') {
                if (!config.selectedOptions || config.selectedOptions.length === 0) {
                    warnings.push(`Add note result node ${node.id} has no selected options`);
                }
            }
        }

        // 验证跳转节点配置
        if (node.type === 'jump') {
            if (!config.targetQuestionId) {
                errors.push(`Jump node ${node.id} is missing targetQuestionId`);
            }
        }

        // 验证触发时机
        if (!config.triggerTiming) {
            errors.push(`Node ${node.id} is missing triggerTiming`);
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
};

/**
 * 验证边的配置
 */
export const validateEdgeConfigurations = (
    edges: Edge[]
): IValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    edges.forEach(edge => {
        if (!edge.data) {
            warnings.push(`Edge ${edge.id} has no data`);
            return;
        }

        if (!edge.data.label && !edge.data.condition) {
            warnings.push(`Edge ${edge.id} has no label or condition`);
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
};

/**
 * 检测循环引用
 */
const detectCycle = (nodes: Node[], edges: Edge[]): boolean => {
    const visited = new Set<string>();
    const recursionStack = new Set<string>();
    
    const dfs = (nodeId: string): boolean => {
        if (recursionStack.has(nodeId)) {
            return true; // 发现循环
        }
        
        if (visited.has(nodeId)) {
            return false; // 已经访问过，无循环
        }
        
        visited.add(nodeId);
        recursionStack.add(nodeId);
        
        // 检查所有从当前节点出发的边
        const outgoingEdges = edges.filter(edge => edge.source === nodeId);
        for (const edge of outgoingEdges) {
            if (dfs(edge.target)) {
                return true;
            }
        }
        
        recursionStack.delete(nodeId);
        return false;
    };
    
    // 从每个未访问的节点开始DFS
    for (const node of nodes) {
        if (!visited.has(node.id)) {
            if (dfs(node.id)) {
                return true;
            }
        }
    }
    
    return false;
};

/**
 * 综合验证
 */
export const validateFlow = (
    nodes: Node[],
    edges: Edge[],
    nodeConfigs: Record<string, any>
): IValidationResult => {
    const structureResult = validateFlowStructure(nodes, edges);
    const nodeConfigResult = validateNodeConfigurations(nodes, nodeConfigs);
    const edgeConfigResult = validateEdgeConfigurations(edges);
    
    return {
        isValid: structureResult.isValid && nodeConfigResult.isValid && edgeConfigResult.isValid,
        errors: [
            ...structureResult.errors,
            ...nodeConfigResult.errors,
            ...edgeConfigResult.errors,
        ],
        warnings: [
            ...structureResult.warnings,
            ...nodeConfigResult.warnings,
            ...edgeConfigResult.warnings,
        ],
    };
};
